import React, { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import { StreamingItem } from '../types'
import { StreamingStatusBadge } from './StatusBadge'
import StreamingManagementService from '../../services/StreamingManagementService'

interface StreamingDetailModalProps {
  open: boolean
  item: StreamingItem | null
  onClose: () => void
}

export default function StreamingDetailModal({ open, item, onClose }: StreamingDetailModalProps) {
  const [provisioned, setProvisioned] = useState<boolean | null>(null)
  const [provisionLoading, setProvisionLoading] = useState(false)
  const [deprovisionLoading, setDeprovisionLoading] = useState(false)
  const [deprovisionError, setDeprovisionError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !item) return

    setProvisioned(null)
    setDeprovisionError(null)

    StreamingManagementService.getStreamingProvisionedStatus(item.streaming_id)
      .then((res) => setProvisioned(res.data.provisioned))
      .catch(() => setProvisioned(false))
  }, [open, item])

  const handleDeprovision = async () => {
    if (!item) return
    setDeprovisionLoading(true)
    setDeprovisionError(null)
    try {
      await StreamingManagementService.deprovisionStreaming(item.streaming_id)
      setProvisioned(false)
    } catch {
      setDeprovisionError('Erro ao desprovisionar o recurso. Tente novamente.')
    } finally {
      setDeprovisionLoading(false)
    }
  }

  if (!item) return null

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'ID do Streaming', value: item.streaming_id },
    { label: 'Evento', value: item.event_name },
    { label: 'Sala', value: item.room_name },
    { label: 'Câmera', value: item.camera_name },
    { label: 'Status', value: <StreamingStatusBadge status={item.status} /> },
    {
      label: 'URL de Streaming',
      value: item.streaming_url ? (
        <a href={item.streaming_url} target="_blank" rel="noopener noreferrer">
          {item.streaming_url}
        </a>
      ) : (
        '—'
      ),
    },
    {
      label: 'URL de Gravação',
      value: item.recording_url ? (
        <a href={item.recording_url} target="_blank" rel="noopener noreferrer">
          {item.recording_url}
        </a>
      ) : (
        '—'
      ),
    },
    { label: 'Criado em', value: new Date(item.created_at).toLocaleString('pt-BR') },
    { label: 'Atualizado em', value: new Date(item.updated_at).toLocaleString('pt-BR') },
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Detalhes do Streaming</DialogTitle>
      <DialogContent dividers>
        {rows.map((row, i) => (
          <Box key={i}>
            <Box display="flex" justifyContent="space-between" py={0.75} flexWrap="wrap" gap={1}>
              <Typography variant="body2" color="text.secondary" minWidth={160}>
                {row.label}
              </Typography>
              <Typography variant="body2" textAlign="right" sx={{ wordBreak: 'break-all' }}>
                {row.value}
              </Typography>
            </Box>
            {i < rows.length - 1 && <Divider />}
          </Box>
        ))}

        <Divider sx={{ my: 1 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" py={0.75} flexWrap="wrap" gap={1}>
          <Typography variant="body2" color="text.secondary" minWidth={160}>
            Recurso GCP Provisionado
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {provisionLoading || provisioned === null ? (
              <CircularProgress size={16} />
            ) : (
              <Typography
                variant="body2"
                fontWeight={600}
                color={provisioned ? 'success.main' : 'text.secondary'}
              >
                {provisioned ? 'Sim' : 'Não'}
              </Typography>
            )}
          </Box>
        </Box>

        {deprovisionError && (
          <Typography variant="caption" color="error" display="block" mt={0.5}>
            {deprovisionError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {provisioned && (
          <Button
            color="error"
            variant="outlined"
            onClick={handleDeprovision}
            disabled={deprovisionLoading}
            startIcon={deprovisionLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {deprovisionLoading ? 'Desprovisionando…' : 'Desprovisionar Recurso'}
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}
